/**
 * The reply recon (ruling R6-C6, docs/57 §5h; prospecting plan §5; docs/12 §9): what a prospect who replied to email
 * one sees on their private page `/p/<slug>`. Built by `@ff/prospects` (recon/) from measured facts only, stored on
 * the prospect page record in KV (`ProspectRecord.recon`), drawn by the app. One KV read per view, like the rest of
 * the prospect page.
 *
 * Every line the page says carries a label (Measured, Observed or Estimate) and at least one source, so a claim can
 * always be traced to the scan, the Google listing, the page of their site or the count it came from. The page's
 * order is fixed by the prospecting plan §5 (R6-02 is still Will's to judge on the real page):
 *
 *   findings  what's costing you calls: at most three, generated by rules, biggest gap first
 *   proof     the maps (up to five keywords), the three shops ahead, their profile and sites against yours
 *   plan      what we'd do, in their towns, and where the searches are (as bands, never as dollars)
 *   then the price, the two promises and the two buttons, which the app draws from its own prices and checkout
 */
import { z } from 'zod';
import { Day } from './common.ts';
import { GridPoints } from './grid-points.ts';

/** Measured by our instruments (the scan, review counts, a load time); observed on their site or Google listing; estimated by a vendor (search volumes). */
export const ReconLabel = z.enum(['measured', 'observed', 'estimate']);
export type ReconLabel = z.infer<typeof ReconLabel>;

export const ReconSourceId = z.string().regex(/^s\d{1,3}$/);

export const ReconSource = z.strictObject({
  id: ReconSourceId,
  /** What it is, in plain words: "Google Maps results from 81 points around your shop". */
  what: z.string().min(3).max(200),
  /** A public address or id a person can check (a page of their site, a profile CID). Never anything private. */
  ref: z.string().max(300).nullable(),
  /** The day it was measured or read. */
  on: Day,
});
export type ReconSource = z.infer<typeof ReconSource>;

export const ReconLine = z.strictObject({
  text: z.string().min(2).max(400),
  label: ReconLabel,
  sources: z.array(ReconSourceId).min(1).max(6),
});
export type ReconLine = z.infer<typeof ReconLine>;

export const ReconTable = z.strictObject({
  caption: z.string().min(3).max(200),
  head: z.array(z.string().max(60)).min(2).max(7),
  /** `self` marks the prospect's own row. */
  rows: z.array(z.strictObject({ cells: z.array(z.string().max(80)).min(2).max(7), self: z.boolean() })).min(1).max(12),
  label: ReconLabel,
  sources: z.array(ReconSourceId).min(1).max(12),
});
export type ReconTable = z.infer<typeof ReconTable>;

/** Which search a grid measured (prospecting plan §5: the core trade term, emergency, two money services, the job title). */
export const ReconGridRole = z.enum(['core', 'emergency', 'service', 'job']);
export type ReconGridRole = z.infer<typeof ReconGridRole>;

export const ReconMap = z.strictObject({
  role: ReconGridRole,
  keyword: z.string().min(2).max(120),
  grid: GridPoints,
  top3Points: z.number().int().min(0).max(81),
  /** "You're in Google's top three at 12 of 81 points." */
  headline: ReconLine,
  /** The towns won and lost, where the pin sits. */
  lines: z.array(ReconLine).max(6),
});
export type ReconMap = z.infer<typeof ReconMap>;

export const ReconFinding = z.strictObject({
  /** A stable rule id, for tests and the follow up email (docs/12 §7.2 "the strongest reason"). */
  rule: z.string().regex(/^[a-z][a-z0-9_]{2,40}$/),
  title: z.string().min(3).max(120),
  lines: z.array(ReconLine).min(1).max(4),
  /** Advice that goes with the finding, not a claim (the category risk, C-8). Null when there's none. */
  note: z.string().max(300).nullable(),
});
export type ReconFinding = z.infer<typeof ReconFinding>;

export const ReconPage = z.strictObject({
  v: z.literal(1),
  builtOn: Day,
  businessName: z.string().min(2).max(60),
  /** The town on their Google listing, for headings. */
  town: z.string().max(80).nullable(),
  findings: z.array(ReconFinding).min(1).max(3),
  maps: z.array(ReconMap).min(1).max(5),
  /** The three shops ahead on the core search, with their row first. */
  rivals: ReconTable.nullable(),
  /** Their Google profile, line by line. */
  profile: z.array(ReconLine).max(6),
  /** Their website against the shops ahead. */
  sites: ReconTable.nullable(),
  siteLines: z.array(ReconLine).max(5),
  /** What we'd do, in their towns. */
  plan: z.array(ReconLine).min(1).max(6),
  /** Searches a month by service and town, as bands. */
  demand: ReconTable.nullable(),
  sources: z.array(ReconSource).min(1).max(80),
});
export type ReconPage = z.infer<typeof ReconPage>;

/** Every source a page cites exists, and every source listed is cited (nothing hidden, nothing dangling). */
export function reconSourceProblems(page: ReconPage): string[] {
  const known = new Set(page.sources.map((s) => s.id));
  const cited = new Set<string>();
  const lines: ReconLine[] = [...page.findings.flatMap((f) => f.lines), ...page.maps.flatMap((m) => [m.headline, ...m.lines]), ...page.profile, ...page.siteLines, ...page.plan];
  const tables = [page.rivals, page.sites, page.demand].filter((t): t is ReconTable => t !== null);
  for (const id of [...lines.flatMap((l) => l.sources), ...tables.flatMap((t) => t.sources)]) cited.add(id);
  const out: string[] = [];
  for (const id of cited) if (!known.has(id)) out.push(`cites ${id}, which isn't in the source list`);
  for (const id of known) if (!cited.has(id)) out.push(`lists ${id}, which nothing cites`);
  return out;
}
